import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import DiagnosticCta from "@/components/DiagnosticCta";
import Seo from "@/components/Seo";
import { HOW_WE_WORK } from "@/data/revamp";
import { breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const RHYTHM = [
  { day: "Monday", item: "Written weekly note — pipeline, progress, blockers, decisions needed" },
  { day: "Mid-week", item: "45-minute cadence call with your nominated decision-maker" },
  { day: "Friday", item: "Dashboard updated — headcount, offers, attrition signals, actions closed" },
  { day: "Monthly", item: "Leadership review against the sprint plan and the 12-month people roadmap" },
];

export default function HowWeWorkPage() {
  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title="How We Work — Diagnose, Build, Hand over"
        description="Orphion engagements run in three phases: Diagnose (one week), Build (scoped sprints with weekly cadence), Hand over (playbooks your team runs). Senior-led, visible weekly, built to end well."
        path="/how-we-work"
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "How We Work", path: "/how-we-work" },
          ]),
        ]}
      />

      <PageHero
        overline="How we work · Engagement model"
        titleA="Diagnose. Build."
        titleAccent="Hand over."
        titleB=""
        intro="Three phases, no mystery. You always know what we're doing, what it costs, and what you need to do — which is usually less than you fear."
      />

      {/* Phases */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 space-y-0">
          {HOW_WE_WORK.map((p, i) => (
            <motion.div
              key={p.phase}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: 0.05 * i, ease: EASE }}
              className="grid md:grid-cols-12 gap-8 py-12 border-t border-[var(--line-strong)] first:border-t-0 first:pt-0"
            >
              <div className="md:col-span-3">
                <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">{p.phase}</p>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] leading-snug">
                  {p.title}
                </h2>
              </div>
              <div className="md:col-span-5">
                <p className="text-[var(--ink-80)] leading-relaxed">{p.body}</p>
              </div>
              <div className="md:col-span-4 border-l-2 border-[var(--accent)] pl-6">
                <p className="overline mb-3">What you do</p>
                <p className="text-sm text-[var(--ink-70)] leading-relaxed">{p.clientDoes}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Weekly rhythm */}
      <section className="py-16 md:py-24 border-b border-[var(--line-strong)] bg-[var(--bg-2)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-4">The rhythm</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-[-0.02em] text-[var(--ink)] mb-12 leading-tight">
            A week inside an{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">engagement</span>.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {RHYTHM.map((r, i) => (
              <motion.div
                key={r.day}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.07 * i, ease: EASE }}
                className="border border-[var(--line-strong)] bg-[var(--bg)] p-6"
              >
                <p className="text-[10px] uppercase tracking-[0.26em] text-[var(--accent)] mb-3">
                  {r.day}
                </p>
                <p className="text-sm text-[var(--ink-80)] leading-relaxed">{r.item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DiagnosticCta />
    </div>
  );
}
